<template>
  <transition name="modal-fade">
    <div v-if="isOpen" class="modal-overlay" @click="handleOverlayClick">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>{{ $t('ageVerificationTitle') }}</h2>
        </div>

        <div class="modal-body">
          <div class="age-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4M12 16h.01"/>
            </svg>
          </div>
          
          <p class="question">{{ $t('ageVerificationQuestion') }}</p>
          
          <div v-if="showRejectionMessage" class="rejection-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M15 9l-6 6M9 9l6 6" />
            </svg>
            <span>{{ $t('ageVerificationRejected') }}</span>
          </div>
        </div>

        <div class="modal-footer">
          <template v-if="showRejectionMessage">
            <button 
              @click="handleClose" 
              class="btn-primary btn-full"
            >
              {{ $t('ok') }}
            </button>
          </template>
          <template v-else>
            <button 
              @click="handleNo" 
              class="btn-secondary"
            >
              {{ $t('ageVerificationNo') }}
            </button>
            <button 
              @click="handleYes" 
              class="btn-primary"
            >
              {{ $t('ageVerificationYes') }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['confirm', 'reject', 'close'])

const showRejectionMessage = ref(false)

// Reset rejection message when modal opens
watch(() => props.isOpen, (newValue) => {
  if (newValue) {
    showRejectionMessage.value = false
  }
})

const handleYes = () => {
  emit('confirm')
}

const handleNo = () => {
  showRejectionMessage.value = true
}

const handleClose = () => {
  emit('reject')
}

const handleOverlayClick = () => {
  if (!showRejectionMessage.value) {
    emit('close')
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  transition: opacity 0.3s ease;
  padding: 1rem;
}

.modal-content {
  background: var(--surface-color);
  width: 100%;
  max-width: 400px;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-lg);
  animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
}

.modal-header {
  padding: 24px 24px 0 24px;
  text-align: center;
}

.modal-header h2 {
  font-size: 1.25rem;
  margin: 0;
  color: var(--secondary-color);
}

.modal-body {
  padding: 24px;
  text-align: center;
}

.age-icon {
  margin-bottom: 20px;
  color: var(--primary-color);
}

.age-icon svg {
  width: 64px;
  height: 64px;
}

.question {
  font-size: 1.1rem;
  color: var(--text-color);
  margin: 0;
  line-height: 1.5;
}

.rejection-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  padding: 16px;
  background: #FEF2F2;
  border-radius: var(--radius-md);
  color: #DC2626;
  font-size: 0.95rem;
  animation: fadeIn 0.3s ease;
}

.rejection-message svg {
  flex-shrink: 0;
}

.modal-footer {
  padding: 16px 24px 24px 24px;
  display: flex;
  gap: 12px;
}

.btn-secondary,
.btn-primary {
  flex: 1;
  padding: 16px 20px;
  border-radius: var(--radius-md);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);
  border: none;
}

.btn-secondary {
  background: #F1F5F9;
  color: var(--text-light);
}

.btn-secondary:hover:not(:disabled) {
  background: #E2E8F0;
}

.btn-primary {
  background: var(--primary-color);
  color: white;
  box-shadow: 0 4px 12px rgba(255, 107, 0, 0.25);
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-dark);
  transform: translateY(-1px);
}

.btn-secondary:disabled,
.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-full {
  flex: 1;
  width: 100%;
}

/* Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@keyframes scaleIn {
  from { 
    opacity: 0;
    transform: scale(0.9); 
  }
  to { 
    opacity: 1;
    transform: scale(1); 
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
